/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useRef, useEffect } from "react";
import { AngularLoaderStore } from "./AngularLoaderStore";
import { useSingleton } from "../injection";
import { windowAsAny } from "../windowAsAny/windowAsAny";
import { propsChangesArray } from "../propsChangesArray/propsChangesArray";
import styled from "@emotion/styled";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericType = any;

interface IProps {
    name: string;
    props?: GenericType;
    componentClassName?: string;
    propsParameter?: string;
}

// eslint-disable-next-line react-compiler/react-compiler
export const AngularBridge = (props: IProps) => {
    "use no memo";

    const angularLoaderStore = useSingleton(AngularLoaderStore);
    reportLoaderToSuspense(angularLoaderStore);

    const isFirstLoadRef = useRef(true);
    const isDestroyedRef = useRef(false);
    const latestPropsRef = useRef(null);
    const containerRef = useRef(null);
    const scopeRef = useRef<GenericType>(null);
    const dataRef = useRef<GenericType>(null);
    const elementRef = useRef<GenericType>(null);

    useEffect(() => {
        isDestroyedRef.current = false;
        latestPropsRef.current = props.props;

        if (isFirstLoadRef.current) {
            // first load
            void angularLoaderStore.loader().then(() => {
                if (isDestroyedRef.current) {
                    return;
                }
                const w = windowAsAny();
                const angularElementService = w.RedDeer.UI.App.getInject(
                    "angularElementService"
                );

                const rootScope = w.RedDeer.UI.App.getInject("$rootScope");
                const scope = rootScope.$new(true);
                scopeRef.current = scope;

                const data = {
                    props: latestPropsRef.current
                };
                dataRef.current = data;

                elementRef.current = angularElementService.createElement({
                    templateHtml: `<${props.name} ${
                        props.propsParameter ?? "props"
                    }="data.props"${
                        props.componentClassName
                            ? ` class="${props.componentClassName}"`
                            : ""
                    } />`,
                    model: {
                        data
                    },
                    appendTo: containerRef.current,
                    parentScope: scope
                });
            });
            isFirstLoadRef.current = false;
        } else {
            // subsequent update
            if (scopeRef.current !== null) {
                dataRef.current.props = latestPropsRef.current;
                scopeRef.current.$safeApply();
            }
        }

        return () => {
            isDestroyedRef.current = true;
        };
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, propsChangesArray(props.props));

    useEffect(() => {
        return () => {
            if (scopeRef.current !== null) {
                windowAsAny().$(elementRef.current).remove();
                const scope = scopeRef.current;
                scopeRef.current = null;
                scope.$destroy();
            }
        };
    }, []);

    return <Div ref={containerRef} />;
};

const Div = styled.div({
    display: "contents"
});

/** communicates with Suspense the loading state of loader() */
function reportLoaderToSuspense(angularLoaderStore: AngularLoaderStore) {
    if (angularLoaderStore.hasLoaded) {
        return;
    }
    loaderResource ??= wrapPromise(angularLoaderStore.loader());
    loaderResource.read();
}
interface IResource<T> {
    read: () => T;
}

let loaderResource: IResource<void> | null = null;

// https://blog.logrocket.com/react-suspense-for-data-fetching/
function wrapPromise<T>(promise: Promise<T>): IResource<T> {
    let status: "pending" | "success" | "error" = "pending";
    let response: T;

    const suspender = promise.then(
        res => {
            status = "success";
            response = res;
        },
        (err: unknown) => {
            status = "error";
            response = err as T;
        }
    );

    const read = () => {
        switch (status) {
            case "pending":
                // eslint-disable-next-line @typescript-eslint/only-throw-error
                throw suspender;
            case "error":
                // eslint-disable-next-line @typescript-eslint/only-throw-error
                throw response;
            default:
                return response;
        }
    };

    return { read };
}
